import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMutation, useQuery } from '@tanstack/react-query';
import { Archive, Banknote, BookOpen, Boxes, Building, Building2, CalendarCheck, ChevronDown, CircleDollarSign, ClipboardList, Cog, Factory, FileBarChart2, FileText, Flame, Hammer, HelpCircle, Landmark, LayoutDashboard, LogOut, Mail, Menu, MessageSquare, Receipt, Search, ShieldCheck, ShoppingBag, ShoppingCart, Sparkles, Users, Workflow, X, } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { CompanySwitcher } from './CompanySwitcher';
import { ErrorBoundary } from './ErrorBoundary';
import { LanguageSelector } from './LanguageSelector';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { NotificationBell } from '@/modules/notifications/NotificationBell';
import { useAuthStore } from '@/stores/auth';
const NAV_GROUPS = [
    {
        label: 'Overview',
        icon: _jsx(LayoutDashboard, { className: "h-4 w-4" }),
        defaultOpen: true,
        items: [{ to: '/', label: 'Dashboard', end: true }],
    },
    {
        label: 'Accounting',
        icon: _jsx(BookOpen, { className: "h-4 w-4" }),
        items: [
            { to: '/accounting/journals', label: 'Journals' },
            { to: '/accounting/vouchers/journal', label: 'Journal Voucher (JV)' },
            { to: '/accounting/vouchers/payment', label: 'Payment Voucher (PV)' },
            { to: '/accounting/vouchers/receipt', label: 'Receipt Voucher (RV)' },
            { to: '/accounting/accounts', label: 'Chart of accounts' },
            { to: '/accounting/gl', label: 'General ledger' },
            { to: '/accounting/trial-balance', label: 'Trial balance' },
            { to: '/accounting/profit-and-loss', label: 'P&L' },
            { to: '/accounting/balance-sheet', label: 'Balance sheet' },
            { to: '/accounting/cash-flow', label: 'Cash flow' },
            { to: '/accounting/cash-flow-forecast', label: 'Cash-flow forecast' },
            { to: '/accounting/budgets', label: 'Budgets vs Actual' },
            { to: '/accounting/recurring', label: 'Recurring templates' },
            { to: '/accounting/depreciation', label: 'Depreciation' },
            { to: '/accounting/kpis', label: 'KPI dashboard' },
            { to: '/accounting/periods', label: 'Periods' },
        ],
    },
    {
        label: 'Sales',
        icon: _jsx(Receipt, { className: "h-4 w-4" }),
        items: [
            { to: '/sales/invoices', label: 'Invoices' },
            { to: '/sales/receivables', label: 'Receivables' },
            { to: '/sales/customers', label: 'Customers' },
            { to: '/sales/ar-aging', label: 'AR aging' },
        ],
    },
    {
        label: 'Purchases',
        icon: _jsx(ShoppingCart, { className: "h-4 w-4" }),
        items: [
            { to: '/purchases/bills', label: 'Bills' },
            { to: '/purchases/subscriptions', label: 'Subscriptions' },
            { to: '/purchases/vendors', label: 'Vendors' },
            { to: '/purchases/ap-aging', label: 'AP aging' },
            { to: '/purchases/wht', label: 'WHT center' },
        ],
    },
    {
        label: 'Inventory',
        moduleKey: 'inventory',
        icon: _jsx(Boxes, { className: "h-4 w-4" }),
        items: [
            { to: '/inventory/items', label: 'Items' },
            { to: '/inventory/levels', label: 'Stock levels' },
            { to: '/inventory/warehouses', label: 'Warehouses' },
            { to: '/inventory/moves', label: 'Movements' },
            { to: '/inventory/reorder', label: 'Reorder suggestions' },
        ],
    },
    {
        label: 'Manufacturing',
        moduleKey: 'manufacturing',
        icon: _jsx(Factory, { className: "h-4 w-4" }),
        items: [
            { to: '/manufacturing/work-orders', label: 'Work orders' },
            { to: '/manufacturing/boms', label: 'BOMs' },
        ],
    },
    {
        label: 'POS',
        moduleKey: 'pos',
        icon: _jsx(ShoppingBag, { className: "h-4 w-4" }),
        items: [{ to: '/pos/registers', label: 'Registers & sessions' }],
    },
    {
        label: 'Projects',
        icon: _jsx(Hammer, { className: "h-4 w-4" }),
        items: [
            { to: '/projects', label: 'Projects', end: true },
            { to: '/projects/tasks', label: 'Tasks' },
            { to: '/projects/time-entries', label: 'Time entries' },
        ],
    },
    {
        label: 'Banking',
        icon: _jsx(Landmark, { className: "h-4 w-4" }),
        items: [
            { to: '/banking/reconciliation', label: 'Reconciliation' },
            { to: '/banking/accounts', label: 'Bank accounts' },
            { to: '/banking/import', label: 'Import statement' },
            { to: '/banking/cash', label: 'Cash dashboard' },
        ],
    },
    {
        label: 'Taxation',
        icon: _jsx(ClipboardList, { className: "h-4 w-4" }),
        items: [
            { to: '/taxation/rates', label: 'Tax rates' },
            { to: '/taxation/wht-sections', label: 'WHT sections' },
            { to: '/taxation/returns', label: 'Tax returns' },
            { to: '/taxation/taxonomy', label: 'Categories & jurisdictions' },
            { to: '/taxation/connect', label: 'Authority connect (HMRC/ZATCA)' },
        ],
    },
    {
        label: 'HR & payroll',
        icon: _jsx(Users, { className: "h-4 w-4" }),
        items: [
            { to: '/hrms/employees', label: 'Employees' },
            { to: '/hrms/org-chart', label: 'Org chart' },
            { to: '/hrms/payroll', label: 'Payroll' },
            { to: '/hrms/salary-tax-brackets', label: 'Salary tax brackets' },
            { to: '/hrms/pay-components', label: 'Pay components / deductions' },
            { to: '/hrms/leave-balances', label: 'Leave balances' },
            { to: '/hrms/performance', label: 'Performance / OKRs' },
            { to: '/hrms/training', label: 'Training' },
            { to: '/hrms/surveys', label: 'Engagement surveys' },
            { to: '/hrms/analytics', label: 'HR analytics' },
            { to: '/hrms/me', label: 'My profile (ESS)' },
        ],
    },
    {
        label: 'Recruiting',
        icon: _jsx(Users, { className: "h-4 w-4" }),
        items: [
            { to: '/hrms/jobs', label: 'Job postings' },
            { to: '/hrms/candidates', label: 'Candidates' },
            { to: '/hrms/applications', label: 'Applications' },
            { to: '/hrms/interviews', label: 'Interviews' },
            { to: '/hrms/onboarding', label: 'Onboarding' },
        ],
    },
    {
        label: 'Attendance',
        icon: _jsx(CalendarCheck, { className: "h-4 w-4" }),
        items: [
            { to: '/attendance/logs', label: 'Clock in/out' },
            { to: '/attendance/leave-requests', label: 'Leave requests' },
            { to: '/attendance/devices', label: 'Biometric devices' },
        ],
    },
    // AI runs by default — no need for a status page in the nav. The route
    // still works at /ai for ops debugging, but it's not surfaced.
    {
        label: 'Assets',
        icon: _jsx(Archive, { className: "h-4 w-4" }),
        items: [
            { to: '/assets/list', label: 'Fixed assets' },
            { to: '/assets/register', label: 'Asset register' },
        ],
    },
    {
        label: 'Real estate',
        moduleKey: 'real_estate',
        icon: _jsx(Building, { className: "h-4 w-4" }),
        items: [
            { to: '/real-estate/properties', label: 'Properties' },
            { to: '/real-estate/units', label: 'Units' },
            { to: '/real-estate/leases', label: 'Leases' },
        ],
    },
    {
        label: 'Documents',
        icon: _jsx(FileText, { className: "h-4 w-4" }),
        items: [
            { to: '/dms', label: 'Document library' },
            { to: '/documents/letters', label: 'Letters & certificates' },
            { to: '/documents/letterheads', label: 'Letterheads' },
            { to: '/documents/vault', label: 'Company vault (KYC)' },
        ],
    },
    {
        label: 'Approvals',
        icon: _jsx(Workflow, { className: "h-4 w-4" }),
        items: [
            { to: '/approvals', label: 'My approvals inbox' },
            { to: '/workflows', label: 'Approval workflow rules' },
        ],
    },
    {
        label: 'Partners',
        icon: _jsx(Sparkles, { className: "h-4 w-4" }),
        items: [{ to: '/partners', label: 'Affiliates & commissions' }],
    },
    {
        label: 'Currencies',
        icon: _jsx(CircleDollarSign, { className: "h-4 w-4" }),
        items: [
            { to: '/currencies/fx-rates', label: 'FX rates' },
            { to: '/currencies/consolidation', label: 'Consolidation' },
        ],
    },
    {
        label: 'Reports',
        icon: _jsx(FileBarChart2, { className: "h-4 w-4" }),
        items: [{ to: '/reports', label: 'Reports', end: true }],
    },
    {
        label: 'Messaging',
        icon: _jsx(Mail, { className: "h-4 w-4" }),
        items: [
            { to: '/messaging/email', label: 'Email setup' },
            { to: '/messaging/whatsapp', label: 'WhatsApp setup' },
            { to: '/messaging/log', label: 'Comms log' },
        ],
    },
    {
        label: 'Billing',
        icon: _jsx(Banknote, { className: "h-4 w-4" }),
        items: [{ to: '/billing', label: 'Subscription', end: true }],
    },
    {
        label: 'Settings',
        icon: _jsx(Cog, { className: "h-4 w-4" }),
        items: [{ to: '/settings', label: 'Workspace settings' }],
    },
];
export function AppShell() {
    // Auto-logout after 1h 24m of UI inactivity.
    useIdleTimeout();
    // Force-change-password gate. Admin reset flips this on the user; the
    // login flow lets them in but they should not be able to touch any
    // module until they've replaced the temp password.
    const navigate = useNavigate();
    const profile = useQuery({
        queryKey: ['me-profile-gate'],
        queryFn: async () => (await api.get('/users/me/')).data,
        staleTime: 60 * 1000,
        retry: false,
    });
    useEffect(() => {
        if (profile.data?.must_change_password) {
            navigate('/force-change-password', { replace: true });
        }
    }, [profile.data?.must_change_password, navigate]);
    const [open, setOpen] = useState(false);
    // The sidebar collapse state survives reloads — wide tables (invoice
    // editor, GL, etc.) need the extra horizontal room and we don't want
    // users to re-collapse on every navigation. localStorage is good
    // enough; the rest of the app shell is unauthenticated by the time
    // this runs.
    const [collapsed, setCollapsed] = useState(() => {
        if (typeof window === 'undefined')
            return false;
        return window.localStorage.getItem('vulcan.sidebar.collapsed') === '1';
    });
    useEffect(() => {
        try {
            window.localStorage.setItem('vulcan.sidebar.collapsed', collapsed ? '1' : '0');
        }
        catch { /* ignore quota / private-mode errors */ }
    }, [collapsed]);
    return (_jsxs("div", { className: "flex min-h-screen bg-muted/30 text-foreground", children: [_jsx(Sidebar, { open: open, onClose: () => setOpen(false), collapsed: collapsed, onToggleCollapse: () => setCollapsed((v) => !v) }), _jsxs("div", { className: cn('flex min-w-0 flex-1 flex-col transition-[margin]', collapsed ? 'md:ms-16' : 'md:ms-64'), children: [_jsx(TopBar, { onMenuClick: () => setOpen(true) }), _jsx("main", { className: "flex-1 px-4 py-6 sm:px-6 lg:px-8", children: _jsx("div", { className: "mx-auto max-w-7xl", children: _jsx(ErrorBoundary, { scope: "module", children: _jsx(Outlet, {}) }) }) })] })] }));
}
function Sidebar({ open, onClose, collapsed, onToggleCollapse, }) {
    // Pull the active company's hidden modules from the dashboard endpoint so
    // services / consultancy companies don't see Manufacturing / POS / etc.
    const dash = useQuery({
        queryKey: ['dashboard'],
        queryFn: async () => (await api.get('/core/dashboard/')).data,
        refetchInterval: 60_000,
        staleTime: 30_000,
    });
    const hidden = new Set(dash.data?.company?.hidden_modules ?? []);
    const visibleGroups = NAV_GROUPS.filter((g) => !g.moduleKey || !hidden.has(g.moduleKey));
    return (_jsxs(_Fragment, { children: [open && (_jsx("div", { className: "fixed inset-0 z-40 bg-foreground/50 backdrop-blur-sm md:hidden", onClick: onClose, "aria-hidden": true })), _jsxs("aside", { className: cn('fixed inset-y-0 start-0 z-50 flex transform flex-col border-e bg-card shadow-lg transition-[width,transform] md:translate-x-0 md:shadow-none', 
                // Mobile slides in/out at full width; desktop honours `collapsed`.
                'w-64', collapsed && 'md:w-16', open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'), children: [_jsxs("div", { className: "flex h-14 items-center justify-between border-b px-3", children: [_jsxs(Link, { to: "/", className: cn('flex items-center gap-2 font-semibold tracking-tight', collapsed && 'md:justify-center md:w-full'), children: [_jsx("span", { className: "flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground", children: _jsx(Flame, { className: "h-4 w-4" }) }), _jsx("span", { className: cn(collapsed && 'md:hidden'), children: "Vulcan ERP" })] }), _jsx("button", { type: "button", onClick: onClose, className: "rounded-md p-1.5 text-muted-foreground hover:bg-muted md:hidden", "aria-label": "Close menu", children: _jsx(X, { className: "h-4 w-4" }) })] }), _jsx("nav", { className: cn('flex-1 space-y-1 overflow-y-auto py-3 text-sm', collapsed ? 'md:px-1.5' : 'px-2'), children: visibleGroups.map((group) => (_jsx(NavGroupBlock, { group: group, onItemClick: onClose, collapsed: collapsed }, group.label))) }), _jsxs("div", { className: cn('flex items-center justify-between border-t px-3 py-3 text-xs text-muted-foreground', collapsed && 'md:flex-col md:gap-2 md:px-2'), children: [_jsx("span", { className: cn(collapsed && 'md:hidden'), children: "v0.1.0 \u00B7 dev" }), _jsx("button", { type: "button", onClick: onToggleCollapse, className: "hidden rounded-md p-1.5 text-muted-foreground hover:bg-muted md:inline-flex", "aria-label": collapsed ? 'Expand sidebar' : 'Collapse sidebar', title: collapsed ? 'Expand sidebar' : 'Collapse sidebar', children: _jsx(ChevronDown, { className: cn('h-4 w-4 transition-transform', collapsed ? '-rotate-90' : 'rotate-90') }) })] })] })] }));
}
function NavGroupBlock({ group, onItemClick, collapsed, }) {
    const [expanded, setExpanded] = useState(group.defaultOpen ?? false);
    // Collapsed mode: first click on the icon pops the subnav as a
    // floating panel. Second click on the icon (or anywhere outside)
    // closes it. If the group has only one item, the icon links straight
    // through.
    const [flyoutOpen, setFlyoutOpen] = useState(false);
    if (collapsed) {
        if (group.items.length === 1) {
            const first = group.items[0];
            return (_jsx(NavLink, { to: first.to, end: first.end, onClick: onItemClick, title: group.label, className: ({ isActive }) => cn('mx-auto my-0.5 flex h-10 w-10 items-center justify-center rounded-md transition-colors', isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'), children: _jsx("span", { className: "shrink-0", children: group.icon }) }));
        }
        return (_jsxs("div", { className: "relative", children: [_jsx("button", { type: "button", onClick: () => setFlyoutOpen((v) => !v), title: group.label, className: "mx-auto my-0.5 flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground", children: _jsx("span", { className: "shrink-0", children: group.icon }) }), flyoutOpen && (_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 z-30", onClick: () => setFlyoutOpen(false) }), _jsxs("div", { className: "absolute start-12 top-0 z-40 min-w-[200px] rounded-md border bg-card p-1 shadow-lg", onMouseLeave: () => setFlyoutOpen(false), children: [_jsx("div", { className: "border-b px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground", children: group.label }), group.items.map((item) => (_jsx(NavLink, { to: item.to, end: item.end, onClick: () => { setFlyoutOpen(false); onItemClick(); }, className: ({ isActive }) => cn('block rounded px-3 py-1.5 text-sm transition-colors', isActive
                                        ? 'bg-primary/10 font-medium text-primary'
                                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'), children: item.label }, item.to)))] })] }))] }));
    }
    // Single-item groups render as a simple link
    if (group.items.length === 1) {
        const item = group.items[0];
        return (_jsxs(NavLink, { to: item.to, end: item.end, onClick: onItemClick, className: ({ isActive }) => cn('flex items-center gap-2 rounded-md px-3 py-2 transition-colors', isActive
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'), children: [_jsx("span", { className: "shrink-0", children: group.icon }), _jsx("span", { className: "truncate", children: item.label })] }));
    }
    return (_jsxs("div", { children: [_jsxs("button", { type: "button", onClick: () => setExpanded((v) => !v), className: "flex w-full items-center gap-2 rounded-md px-3 py-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground", children: [_jsx("span", { className: "shrink-0", children: group.icon }), _jsx("span", { className: "flex-1 truncate text-start font-medium", children: group.label }), _jsx(ChevronDown, { className: cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180') })] }), expanded && (_jsx("div", { className: "ms-3 mt-1 space-y-0.5 border-s border-border ps-3", children: group.items.map((item) => (_jsx(NavLink, { to: item.to, end: item.end, onClick: onItemClick, className: ({ isActive }) => cn('block rounded-md px-3 py-1.5 text-xs transition-colors', isActive
                        ? 'bg-primary/10 font-medium text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'), children: item.label }, item.to))) }))] }));
}
function TopBar({ onMenuClick }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const logout = useAuthStore((s) => s.logout);
    const me = useQuery({
        queryKey: ['auth-me'],
        queryFn: async () => (await api.get('/auth/me/')).data,
        staleTime: 5 * 60 * 1000,
        retry: false,
    });
    const userEmail = me.data?.email;
    const [profileOpen, setProfileOpen] = useState(false);
    const logoutMutation = useMutation({
        mutationFn: async () => {
            await api.post('/auth/logout/');
        },
        onSettled: () => {
            logout();
            navigate('/login', { replace: true });
        },
    });
    const initials = (userEmail ?? 'u@v').slice(0, 2).toUpperCase();
    return (_jsxs("header", { className: "sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur sm:px-6", children: [_jsx("button", { type: "button", onClick: onMenuClick, className: "rounded-md p-2 text-muted-foreground hover:bg-muted md:hidden", "aria-label": "Open menu", children: _jsx(Menu, { className: "h-5 w-5" }) }), _jsx("div", { className: "hidden md:flex md:w-full md:max-w-md", children: _jsxs("div", { className: "relative w-full", children: [_jsx(Search, { className: "pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" }), _jsx("input", { type: "search", placeholder: t('app.search') ?? 'Search…', className: "h-9 w-full rounded-md border border-input bg-muted/30 ps-9 pe-3 text-sm placeholder:text-muted-foreground focus-visible:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" })] }) }), _jsxs("div", { className: "ms-auto flex items-center gap-1.5", children: [_jsx(CompanyLogoMark, {}), _jsx(CompanySwitcher, {}), _jsx(LanguageSelector, {}), _jsx("div", { className: "hidden sm:block", children: _jsx(NotificationBell, {}) }), _jsx("button", { type: "button", className: "hidden rounded-md p-2 text-muted-foreground hover:bg-muted sm:inline-flex", "aria-label": "Help", children: _jsx(HelpCircle, { className: "h-4 w-4" }) }), _jsxs("div", { className: "relative", children: [_jsxs("button", { type: "button", onClick: () => setProfileOpen((v) => !v), className: "flex items-center gap-2 rounded-md p-1 hover:bg-muted", "aria-label": "Profile", children: [_jsx("span", { className: "flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary", children: initials }), _jsx(ChevronDown, { className: "hidden h-3.5 w-3.5 text-muted-foreground sm:block" })] }), profileOpen && (_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 z-40", onClick: () => setProfileOpen(false), "aria-hidden": true }), _jsxs("div", { className: "absolute end-0 top-full z-50 mt-1 w-56 rounded-lg border bg-card p-1 shadow-md", children: [_jsx("div", { className: "border-b px-3 py-2 text-xs text-muted-foreground", children: userEmail ?? 'Signed in' }), _jsxs("button", { type: "button", onClick: () => { setProfileOpen(false); navigate('/settings'); }, className: "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-muted", children: [_jsx(ShieldCheck, { className: "h-4 w-4" }), " Settings"] }), _jsxs("button", { type: "button", onClick: () => { setProfileOpen(false); navigate('/settings/2fa'); }, className: "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-muted", children: [_jsx(MessageSquare, { className: "h-4 w-4" }), " Two-factor auth"] }), _jsx("div", { className: "my-1 border-t" }), _jsxs("button", { type: "button", onClick: () => { setProfileOpen(false); logoutMutation.mutate(); }, disabled: logoutMutation.isPending, className: "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-red-600 hover:bg-red-500/10 disabled:opacity-50", children: [_jsx(LogOut, { className: "h-4 w-4" }), " Sign out"] })] })] }))] })] })] }));
}
export function CompanyChip({ name }) {
    return (_jsxs("span", { className: "inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs", children: [_jsx(Building2, { className: "h-3 w-3" }), " ", name] }));
}
/* Small badge that displays the active company's uploaded logo in the
 * top bar. Falls back gracefully when the logo isn't uploaded yet —
 * we don't want a broken-image placeholder, so we just render nothing
 * in that case. The logo is pulled from the company-detail endpoint
 * (cached, no extra requests on top of what the page already runs). */
function CompanyLogoMark() {
    const activeId = useAuthStore((s) => s.activeCompanyId);
    const detail = useQuery({
        queryKey: ['company-detail', activeId],
        queryFn: async () => (await api.get(`/tenants/companies/${activeId}/`)).data,
        enabled: Boolean(activeId),
        staleTime: 5 * 60 * 1000,
    });
    const logoUrl = detail.data?.logo || null;
    if (!logoUrl)
        return null;
    return (_jsx("span", { className: "hidden h-8 w-8 items-center justify-center overflow-hidden rounded-md border border-input bg-card sm:inline-flex", title: detail.data?.legal_name || detail.data?.trade_name || '', children: _jsx("img", { src: logoUrl, alt: "company logo", className: "h-full w-full object-contain" }) }));
}
